import { useState, useEffect } from 'react';
import { getAnalyticsEvents } from '../../services/analyticsService';
import { getCollection } from '../../services/firestoreService';
import { getCrashAnalytics } from '../../services/crashAnalyticsService';
import { subDays, format } from 'date-fns';
import BarChart from '../charts/BarChart';
import LineChart from '../charts/LineChart';

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  color: string;
}

const KPICard: React.FC<KPICardProps> = ({ title, value, subtitle, color }) => (
  <div style={{
    backgroundColor: 'white',
    padding: '1.5rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    borderLeft: `4px solid ${color}`,
  }}>
    <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>{title}</p>
    <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>{value}</h2>
    {subtitle && <p style={{ margin: 0, color: '#999', fontSize: '0.75rem' }}>{subtitle}</p>}
  </div>
);

const SystemHealthTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [errorRate, setErrorRate] = useState(0);
  const [totalErrors, setTotalErrors] = useState(0);
  const [totalEvents, setTotalEvents] = useState(0);
  const [errorEvents, setErrorEvents] = useState<any[]>([]);
  const [errorTypes, setErrorTypes] = useState<Map<string, number>>(new Map());
  const [errorSeverity, setErrorSeverity] = useState<Map<string, number>>(new Map());
  const [errorTrend, setErrorTrend] = useState<Map<string, number>>(new Map());
  const [costData, setCostData] = useState<any[]>([]);
  const [costAnomalies, setCostAnomalies] = useState<any[]>([]);
  const [performanceMetrics, setPerformanceMetrics] = useState<any[]>([]);
  const [crashData, setCrashData] = useState<Awaited<ReturnType<typeof getCrashAnalytics>> | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Get all events from last 30 days
        const allEvents = await getAnalyticsEvents(30);
        setTotalEvents(allEvents.length);

        // Get error events
        const errors = allEvents.filter(
          (e) => e.category === 'error' || e.eventName.includes('error')
        );
        setTotalErrors(errors.length);
        setErrorRate(allEvents.length > 0 ? (errors.length / allEvents.length) * 100 : 0);

        // Count error types
        const errorTypeCounts = new Map<string, number>();
        errors.forEach((error) => {
          const errorType = error.properties.error_type as string || 
                          error.properties.error_code as string || 
                          (error.properties.error_message as string)?.substring(0, 30) ||
                          'Unknown';
          errorTypeCounts.set(errorType, (errorTypeCounts.get(errorType) || 0) + 1);
        });
        setErrorTypes(errorTypeCounts);

        // Count error severity (if available)
        const severityCounts = new Map<string, number>();
        errors.forEach((error) => {
          const errorType = error.properties.error_type as string || '';
          const severity = error.properties.severity as string || 
                          error.properties.error_severity as string ||
                          (errorType?.includes('critical') ? 'Critical' :
                           errorType?.includes('fatal') ? 'Critical' :
                           'Medium');
          severityCounts.set(severity, (severityCounts.get(severity) || 0) + 1);
        });
        setErrorSeverity(severityCounts);

        // Error trend over time
        const trend = new Map<string, number>();
        errors.forEach((error) => {
          const date = format(new Date(error.timestamp), 'yyyy-MM-dd');
          trend.set(date, (trend.get(date) || 0) + 1);
        });
        setErrorTrend(trend);

        // Get recent error events with full details
        setErrorEvents(errors.slice(0, 50));

        // Get cost analytics
        try {
          const costs = await getCollection('cost_analytics', undefined, undefined, 'desc', 100);
          setCostData(costs);
          
          // Detect cost anomalies (spikes)
          if (costs.length > 1) {
            const avgCost = costs.reduce((sum: number, cost: any) => {
              const costValue = cost.total_cost || cost.cost || cost.amount || 0;
              return sum + (typeof costValue === 'number' ? costValue : 0);
            }, 0) / costs.length;
            
            const anomalies = costs.filter((cost: any) => {
              const costValue = cost.total_cost || cost.cost || cost.amount || 0;
              return typeof costValue === 'number' && costValue > avgCost * 1.5;
            });
            setCostAnomalies(anomalies);
          }
        } catch (e) {
          console.warn('Could not load cost analytics:', e);
        }

        // Get performance metrics
        try {
          const perfEvents = allEvents.filter(
            (e) => e.category === 'performance' || e.eventName.includes('performance')
          );
          setPerformanceMetrics(perfEvents);
        } catch (e) {
          console.warn('Could not load performance metrics:', e);
        }

        // Get security events count
        try {
          const securityEvents = await getCollection('security_events', undefined, undefined, 'desc', 100);
          // Could add security metrics here
        } catch (e) {
          console.warn('Could not load security events:', e);
        }

        // Crashes & stability (app_crashes, crash_stats from Flutter advanced_error_handler)
        try {
          const crash = await getCrashAnalytics(30);
          setCrashData(crash);
        } catch (e) {
          console.warn('Could not load crash analytics:', e);
        }
      } catch (error) {
        console.error('Error loading system health data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading system health data...</p>
      </div>
    );
  }

  const errorTypeData = {
    labels: Array.from(errorTypes.keys()).slice(0, 10),
    datasets: [{
      label: 'Error Count',
      data: Array.from(errorTypes.values()).slice(0, 10),
      backgroundColor: '#ef4444',
    }],
  };

  const severityData = {
    labels: Array.from(errorSeverity.keys()),
    datasets: [{
      label: 'Error Count by Severity',
      data: Array.from(errorSeverity.values()),
      backgroundColor: ['#dc2626', '#f59e0b', '#10b981', '#6366f1'],
    }],
  };

  const trendChartData = {
    labels: Array.from(errorTrend.keys()).sort(),
    datasets: [{
      label: 'Errors per Day',
      data: Array.from(errorTrend.keys())
        .sort()
        .map(date => errorTrend.get(date) || 0),
      borderColor: '#ef4444',
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      tension: 0.4,
    }],
  };

  const criticalErrors = errorSeverity.get('Critical') || 0;
  const highErrors = errorSeverity.get('High') || 0;
  const systemHealth = errorRate < 1 ? 'excellent' : errorRate < 5 ? 'good' : errorRate < 10 ? 'fair' : 'poor';

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        System Health & Performance
      </h1>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <KPICard
          title="System Health"
          value={systemHealth.toUpperCase()}
          subtitle={`${errorRate.toFixed(2)}% error rate`}
          color={errorRate < 1 ? '#10b981' : errorRate < 5 ? '#f59e0b' : '#ef4444'}
        />
        <KPICard
          title="Error Rate"
          value={`${errorRate.toFixed(2)}%`}
          subtitle={`${totalErrors} of ${totalEvents.toLocaleString()} events`}
          color={errorRate < 1 ? '#10b981' : errorRate < 5 ? '#f59e0b' : '#ef4444'}
        />
        <KPICard
          title="Total Errors"
          value={totalErrors}
          subtitle="Last 30 days"
          color={totalErrors < 100 ? '#10b981' : totalErrors < 500 ? '#f59e0b' : '#ef4444'}
        />
        <KPICard
          title="Critical Errors"
          value={criticalErrors}
          subtitle={criticalErrors > 0 ? '⚠️ Immediate attention' : 'None'}
          color={criticalErrors > 0 ? '#ef4444' : '#10b981'}
        />
        <KPICard
          title="High Severity Errors"
          value={highErrors}
          subtitle="Requires review"
          color={highErrors > 0 ? '#f59e0b' : '#10b981'}
        />
        <KPICard
          title="Cost Anomalies"
          value={costAnomalies.length}
          subtitle={costAnomalies.length > 0 ? '⚠️ Review needed' : 'Normal'}
          color={costAnomalies.length > 0 ? '#f59e0b' : '#10b981'}
        />
        <KPICard
          title="Performance Events"
          value={performanceMetrics.length}
          subtitle="Last 30 days"
          color="#6366f1"
        />
        <KPICard
          title="Total Events"
          value={totalEvents.toLocaleString()}
          subtitle="Last 30 days"
          color="#3b82f6"
        />
        {crashData && (
          <>
            <KPICard
              title="Crashes (24h)"
              value={crashData.totalLast24h}
              subtitle={crashData.totalLast24h > 0 ? '⚠️ Review below' : 'None'}
              color={crashData.totalLast24h > 0 ? '#f59e0b' : '#10b981'}
            />
            <KPICard
              title="Crashes (7d)"
              value={crashData.totalLast7d}
              subtitle="App stability"
              color={crashData.totalLast7d > 10 ? '#ef4444' : crashData.totalLast7d > 0 ? '#f59e0b' : '#10b981'}
            />
            <KPICard
              title="Crashes (30d)"
              value={crashData.totalLast30d}
              subtitle="From app_crashes"
              color="#6366f1"
            />
          </>
        )}
      </div>

      {/* Crashes & Stability */}
      {crashData && (crashData.crashes.length > 0 || crashData.statsByDay.length > 0) && (
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '2rem',
        }}>
          <h3 style={{ marginTop: 0 }}>Crashes & Stability (app_crashes / crash_stats)</h3>
          {crashData.statsByDay.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>Crashes per day</h4>
              <LineChart
                data={{
                  labels: crashData.statsByDay.map((s) => s.date).reverse(),
                  datasets: [{
                    label: 'Crashes',
                    data: crashData.statsByDay.map((s) => s.total_crashes).reverse(),
                    borderColor: '#ef4444',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  }],
                }}
                height={220}
              />
            </div>
          )}
          {Object.keys(crashData.bySeverity).length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>By severity</h4>
              <BarChart
                data={{
                  labels: Object.keys(crashData.bySeverity),
                  datasets: [{ label: 'Count', data: Object.values(crashData.bySeverity), backgroundColor: '#ef4444' }],
                }}
                height={200}
              />
            </div>
          )}
          {Object.keys(crashData.byPlatform).length > 0 && (
            <p style={{ margin: '0.5rem 0', fontSize: '0.875rem', color: '#666' }}>
              By platform: {Object.entries(crashData.byPlatform).map(([p, n]) => `${p}: ${n}`).join(', ')}
            </p>
          )}
          {crashData.crashes.length > 0 && (
            <>
              <h4 style={{ margin: '1rem 0 0.5rem 0', fontSize: '0.95rem' }}>Recent crashes</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>Time</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>Severity</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>Platform</th>
                    <th style={{ padding: '0.5rem', textAlign: 'left' }}>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {crashData.crashes.slice(0, 20).map((c) => (
                    <tr key={c.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '0.5rem' }}>{format(c.timestamp, 'MMM d, HH:mm')}</td>
                      <td style={{ padding: '0.5rem' }}>
                        <span style={{
                          padding: '0.2rem 0.4rem',
                          borderRadius: '4px',
                          backgroundColor: c.severity === 'critical' ? '#fee2e2' : '#fef3c7',
                          fontSize: '0.75rem',
                        }}>{c.severity || '—'}</span>
                      </td>
                      <td style={{ padding: '0.5rem' }}>{c.device_info?.platform ?? '—'}</td>
                      <td style={{ padding: '0.5rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }} title={c.message}>
                        {c.message?.substring(0, 60) || '—'}{c.message && c.message.length > 60 ? '…' : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* Error Trend */}
      {errorTrend.size > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '2rem',
        }}>
          <h3 style={{ marginTop: 0 }}>Error Trend (Last 30 Days)</h3>
          <LineChart data={trendChartData} height={300} />
        </div>
      )}

      {/* Error Types Distribution */}
      {errorTypes.size > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '2rem',
        }}>
          <h3 style={{ marginTop: 0 }}>Error Types Distribution</h3>
          <BarChart data={errorTypeData} height={300} />
        </div>
      )}

      {/* Error Severity Distribution */}
      {errorSeverity.size > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '2rem',
        }}>
          <h3 style={{ marginTop: 0 }}>Error Severity Distribution</h3>
          <BarChart data={severityData} height={300} />
        </div>
      )}

      {/* Cost Anomalies Alert */}
      {costAnomalies.length > 0 && (
        <div style={{
          backgroundColor: '#fef3c7',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid #f59e0b',
          marginBottom: '2rem',
        }}>
          <h3 style={{ marginTop: 0, color: '#f59e0b' }}>⚠️ Cost Anomalies Detected</h3>
          <p style={{ margin: '0.5rem 0' }}>
            {costAnomalies.length} cost anomaly(ies) detected. Review the cost data below.
          </p>
        </div>
      )}

      {/* Critical Errors Alert */}
      {criticalErrors > 0 && (
        <div style={{
          backgroundColor: '#fef2f2',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid #ef4444',
          marginBottom: '2rem',
        }}>
          <h3 style={{ marginTop: 0, color: '#ef4444' }}>🚨 Critical Errors Detected</h3>
          <p style={{ margin: '0.5rem 0' }}>
            {criticalErrors} critical error(s) require immediate attention.
          </p>
        </div>
      )}

      {/* Recent Errors Table */}
      {errorEvents.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          marginBottom: '2rem',
        }}>
          <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
            Recent Errors
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Time</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Event</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Error Type</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Severity</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>User ID</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {errorEvents.map((error, index) => {
                const errorType = error.properties.error_type as string || '';
                const severity = error.properties.severity as string || 
                                error.properties.error_severity as string ||
                                (errorType?.includes('critical') ? 'Critical' : 'Medium');
                const isCritical = severity === 'Critical';
                
                return (
                  <tr 
                    key={index} 
                    style={{ 
                      borderBottom: '1px solid #f0f0f0',
                      backgroundColor: isCritical ? '#fef2f2' : 'transparent',
                    }}
                  >
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      {format(new Date(error.timestamp), 'MMM dd, yyyy HH:mm')}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{error.eventName}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      {error.properties.error_type as string || 
                       error.properties.error_code as string || 
                       (error.properties.error_message as string)?.substring(0, 30) ||
                       'Unknown'}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        backgroundColor: isCritical ? '#fee2e2' : '#fef3c7',
                        color: isCritical ? '#991b1b' : '#92400e',
                        fontSize: '0.75rem',
                      }}>
                        {severity}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                      {error.userId?.substring(0, 8) || 'N/A'}...
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      {JSON.stringify(error.properties || {}).substring(0, 50)}...
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Cost Analytics */}
      {costData.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          marginBottom: '2rem',
        }}>
          <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
            Cost Analytics
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Date</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Total Cost</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>API Calls</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {costData.slice(0, 20).map((cost: any, index: number) => {
                const costValue = cost.total_cost || cost.cost || cost.amount || 0;
                const isAnomaly = costAnomalies.some(a => a.id === cost.id);
                
                return (
                  <tr 
                    key={index} 
                    style={{ 
                      borderBottom: '1px solid #f0f0f0',
                      backgroundColor: isAnomaly ? '#fef3c7' : 'transparent',
                    }}
                  >
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      {cost.timestamp?.toDate 
                        ? format(cost.timestamp.toDate(), 'MMM dd, yyyy')
                        : cost.date || 'N/A'}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      ${typeof costValue === 'number' ? costValue.toFixed(2) : '0.00'}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      {cost.api_calls || cost.apiCalls || cost.total_calls || 'N/A'}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      {isAnomaly && (
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          backgroundColor: '#fef3c7',
                          color: '#92400e',
                          fontSize: '0.75rem',
                        }}>
                          Anomaly
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {errorEvents.length === 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>✅ System Healthy</h3>
          <p style={{ color: '#666' }}>No errors detected in the last 30 days. System is operating normally.</p>
        </div>
      )}
    </div>
  );
};

export default SystemHealthTab;
